import * as React from 'react';

import { Box, Button, Paper } from '@mui/material';

import FormPaper from '../FormPaper';

import { getColor } from '../../core/colors';
import { Incident, IncidentStatus } from '../../core/api/clients';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import useNavigateWithConfirm from '../../core/hooks/useNavigateWithConfirm';

import IncidentFormFields from './IncidentFormFields';
import IncidentFormReports from './IncidentFormReports';
import IncidentReopenButton from './IncidentReopenButton';
import ConfirmationDialog from '../ConfirmationDialog';

interface IncidentFormBodyProps {
  incident: Incident;
  onReopen?: (reopenReason: string) => void;
  readOnly?: boolean;
  onDelete: () => void;
}

function IncidentFormBody({ incident, onReopen, readOnly, onDelete }: IncidentFormBodyProps): React.JSX.Element {
  const authorizedFormData = useAuthorizedFormData();
  const navigateWithConfirm = useNavigateWithConfirm();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  const handleClose = () => navigateWithConfirm(authorizedFormData.metadata?.route ?? '');
  const handleReopen = (reopenReason: string) => {
    if (onReopen) {
      onReopen(reopenReason);
    }
  };

  return (
    <FormPaper color={getColor(authorizedFormData.metadata?.color ?? 'grey')}>
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        setOpen={setShowDeleteDialog}
        confirm={onDelete}
        text="Are you sure you want to permanently delete this incident?"
      />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <IncidentFormFields
          incidentStatus={incident.status}
          incidentAssessedOn={incident.assessedOn}
          readOnly={readOnly}
        />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
        <IncidentFormReports
          attachedReports={incident.eventReports}
          incidentStatus={incident.status}
          primaryReportId={incident.primaryEventReportId}
          readOnly={readOnly}
        />
      </Paper>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleClose}>Close</Button>
        <Box display="flex">
          <Box mr={3}>
            { !readOnly && <Button variant="contained" color="error" onClick={() => setShowDeleteDialog(true)}>Delete</Button> }
          </Box>

          {incident.status === IncidentStatus.Open && !readOnly && (
          <Button type="submit">Close Incident</Button>
          )}
          {incident.status === IncidentStatus.Closed && (
          <IncidentReopenButton onReopen={handleReopen} />
          )}
        </Box>
      </Box>
    </FormPaper>
  );
}

export default IncidentFormBody;
